import { useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ExternalLink, ScrollText, ChevronDown, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Role } from '@nexa/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { RoleGate } from '@/auth/role-gate';
import { useAuditLogPaginated, type AuditRecord } from './use-audit';

// Audit tab per docs/ui/operator-ui-tds.md §4.2 + §8 (j3).
//
// NEX-614: Now supports pagination to handle large audit logs (#744).
// PAYMENT_AUTHORIZED events are filtered out to reduce noise from wallet
// card issuance. "Walk correlation" affordance jumps into admin-ui in a new
// tab — visible only when the operator has Role.ADMIN AND belongs to org_nexa.

const ADMIN_UI_BASE = import.meta.env.VITE_ADMIN_UI_BASE_URL ?? 'https://admin.nexa.studio';

/** NEX-614: Map audit action code to i18n key */
const ACTION_KEY: Record<string, string> = {
  CASE_CREATED: 'caseCreated',
  REBOOKING_SELECTED: 'rebookingSelected',
  NOTIFICATION_DISPATCHED: 'notificationDispatched',
  TICKET_ISSUE: 'ticketIssue',
  HOTEL_BOOK: 'hotelBook',
  HOTEL_HELD: 'hotelHeld',
  HOTEL_ALLOCATE: 'hotelAllocate',
  VOUCHER_ISSUE: 'voucherIssue',
  STATE_TRANSITION: 'stateTransition',
  OFFER_PROPOSED: 'offerProposed',
  OFFER_ACCEPTED: 'offerAccepted',
  OFFER_DECLINED: 'offerDeclined',
  CONTACT_UPDATED: 'contactUpdated',
  TRANSPORT_OUTCOME: 'transportOutcome',
  PASSENGER_CHECK_IN: 'passengerCheckIn',
  CREATE: 'create',
  UPDATE: 'update',
  EXECUTE: 'execute',
};

/**
 * NEX-744: Format correlation URN for display.
 * Returns "—" for unknown or empty correlation values.
 */
function formatCorrelation(correlationUrn: string): { hasCorrelation: boolean; display: string } {
  if (!correlationUrn || correlationUrn === 'urn:correlation:unknown') {
    return { hasCorrelation: false, display: '—' };
  }
  return { hasCorrelation: true, display: correlationUrn };
}

interface AuditTabProps {
  caseUrn: string;
}

export function AuditTab({ caseUrn }: AuditTabProps) {
  const { t } = useTranslation('caseWork');
  const { user } = useAuth0();
  const log = useAuditLogPaginated(caseUrn, { excludePayments: true });
  const isNexaStaff =
    typeof user?.[`https://nexa/airline`] === 'string' &&
    (user[`https://nexa/airline`] as string).toLowerCase().includes('nexa');

  const records = useMemo<AuditRecord[]>(() => {
    return log.data?.pages.flat() ?? [];
  }, [log.data]);

  /** NEX-614: Format action code using i18n */
  const formatAction = (action: string): string => {
    const key = ACTION_KEY[action];
    if (key) {
      return t(`audit.action.${key}`);
    }
    return action.replace(/_/g, ' ').toLowerCase();
  };

  if (log.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-16 w-full max-w-3xl" />
        <Skeleton className="h-16 w-full max-w-3xl" />
        <Skeleton className="h-16 w-full max-w-3xl" />
      </div>
    );
  }

  if (log.isError) {
    return (
      <Card className="max-w-3xl">
        <CardContent className="p-6 text-sm text-destructive">{t('audit.loadError')}</CardContent>
      </Card>
    );
  }

  if (records.length === 0) {
    return <EmptyState icon={<ScrollText className="h-6 w-6" />} title={t('audit.empty')} />;
  }

  return (
    <Card className="max-w-3xl">
      <CardHeader>
        <CardTitle className="text-sm">{t('audit.title', { n: records.length })}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-y divide-border" data-testid="audit-list">
          {records.map((r) => (
            <li key={r.auditUrn} className="p-4 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{formatAction(r.action)}</span>
                    <span className="text-xs text-muted-foreground">
                      {r.operatorDisplayName}
                    </span>
                  </div>
                  {r.targetUrn ? (
                    <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground" title={r.targetUrn}>
                      {t('audit.target', { urn: r.targetUrn })}
                    </p>
                  ) : null}
                  {r.detailsMasked ? (
                    <p className="mt-0.5 text-xs text-muted-foreground">{r.detailsMasked}</p>
                  ) : null}
                  {(() => {
                    const { hasCorrelation, display } = formatCorrelation(r.correlationUrn);
                    return (
                      <p
                        className="mt-1 truncate font-mono text-[10px] text-muted-foreground"
                        title={hasCorrelation ? r.correlationUrn : undefined}
                      >
                        {hasCorrelation ? t('shared.correlation', { urn: display }) : display}
                      </p>
                    );
                  })()}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1 text-xs text-muted-foreground">
                  <time dateTime={r.ts}>{new Date(r.ts).toLocaleTimeString()}</time>
                  {isNexaStaff ? (
                    <RoleGate min={Role.ADMIN} hideWhenDenied>
                      <a
                        href={`${ADMIN_UI_BASE}/correlation/${encodeURIComponent(r.correlationUrn)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline"
                      >
                        {t('audit.walkCorrelation')}
                        <ExternalLink className="h-3 w-3" aria-hidden />
                      </a>
                    </RoleGate>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
        {log.hasNextPage && (
          <div className="border-t border-border p-3">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => log.fetchNextPage()}
              disabled={log.isFetchingNextPage}
            >
              {log.isFetchingNextPage ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('audit.loading')}
                </>
              ) : (
                <>
                  <ChevronDown className="mr-2 h-4 w-4" />
                  {t('audit.loadMore')}
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
